// =============================================================
// BTR Companion — adaptateurs API -> formes proches du mock
// (limite les changements dans les écrans : mêmes noms de champs.)
// =============================================================
import { getChamp } from './ddragon';
import { deriveTier, laneLabel, fmtDuration, timeAgo } from './format';

/** Liste de champions : analytics + positions + ddragon -> shape mock CHAMPIONS. */
export function adaptChampions(apiChamps = [], positions = {}, totalMatches = 0) {
  return (apiChamps || [])
    .map((c) => {
      const dd = getChamp(Number(c.champion_id)) || getChamp(c.champion_name);
      if (!dd) return null;
      const games = Number(c.games) || 0;
      const winrate = Number(c.winrate) || 0;
      const pos = positions[c.champion_id] || positions[String(c.champion_id)] || [];
      return {
        id: dd.id,
        championId: dd.key,
        name: dd.name,
        title: dd.title,
        tags: dd.tags,
        roles: pos.map(laneLabel),
        difficulty: dd.info?.difficulty ?? 5,
        winrate,
        pickrate: totalMatches ? +((games / totalMatches) * 100).toFixed(1) : 0,
        banrate: null,
        tier: deriveTier(winrate, games),
        games,
        kda: Number(c.avg_kda) || 0,
      };
    })
    .filter(Boolean);
}

/** Détail champion : analytics/champions/:id + ddragon. */
export function adaptChampionDetail(api, totalMatches = 0) {
  const dd = getChamp(api.champion_name) || getChamp(Number(api.champion_id)) || {};
  const games = Number(api.games) || 0;
  const winrate = Number(api.winrate) || 0;
  const byPos = api.by_position || {};
  // Rôles réellement joués : ≥ 15 % des parties du champion (le rôle principal
  // est toujours conservé) — évite « Ahri Jungle » pour 78 parties sur 145 000.
  const posEntries = Object.entries(byPos).sort((a, b) => (b[1]?.games || 0) - (a[1]?.games || 0));
  const posTotal = posEntries.reduce((s, [, d]) => s + (d?.games || 0), 0) || 1;
  const roles = posEntries
    .filter(([, d], i) => i === 0 || (d?.games || 0) >= posTotal * 0.15)
    .map(([pos]) => laneLabel(pos));
  return {
    id: dd.id || api.champion_name,
    championId: dd.key,
    name: dd.name || api.champion_name,
    title: dd.title || '',
    tags: dd.tags || [],
    roles,
    difficulty: dd.info?.difficulty ?? 5,
    winrate,
    pickrate: totalMatches ? +((games / totalMatches) * 100).toFixed(1) : 0,
    banrate: null,
    tier: deriveTier(winrate, games),
    games,
    kda: Number(api.avg_kda) || 0,
    kills: Number(api.avg_kills) || 0,
    deaths: Number(api.avg_deaths) || 0,
    assists: Number(api.avg_assists) || 0,
    byPosition: byPos,
  };
}

/** Un matchup (analytics/matchups) -> {id,name,title,winrate,games}. */
export function adaptMatchup(m) {
  const dd = getChamp(Number(m.opponent_id)) || getChamp(m.opponent_name) || {};
  return {
    id: dd.id || m.opponent_name,
    name: dd.name || m.opponent_name,
    title: dd.title || '',
    winrate: Number(m.winrate) || 0,
    games: Number(m.games) || 0,
  };
}

/** Profil : enrichit la réponse /api/summoner (matchs lisibles). */
export function adaptSummoner(data) {
  const solo = data.ranks?.find((r) => r.queueType === 'RANKED_SOLO_5x5') || null;
  const flex = data.ranks?.find((r) => r.queueType === 'RANKED_FLEX_SR') || null;
  const QUEUES = { 400: 'Normale', 420: 'Solo/Duo', 430: 'Normale', 440: 'Flexible', 450: 'ARAM', 490: 'Normale', 700: 'Clash', 1700: 'Arena', 1710: 'Arena', 1900: 'URF' };
  const matches = (data.matches || []).map((m) => ({
    ...m,
    queue: QUEUES[m.queueId] || 'Partie',
    duration: fmtDuration(m.durationSec),
    when: timeAgo(m.gameCreation),
    // L'API renvoie l'identifiant interne Riot ("XinZhao", "Chogath") : on affiche le nom ddragon.
    champion: (getChamp(Number(m.championId)) || {}).id || m.champion,
    championName: (getChamp(Number(m.championId)) || {}).name || m.champion,
    roleLabel: laneLabel(m.role),
  }));
  const topChamps = (data.topChamps || []).map((c) => {
    const dd = getChamp(Number(c.championId)) || getChamp(c.id) || null;
    return { ...c, id: dd?.id || c.id, name: dd?.name || c.name };
  });
  return { ...data, solo, flex, matches, topChamps };
}

/** Détail de partie : /api/match -> shape mock (blueTeam/redTeam). */
export function adaptMatch(api, mePuuid = null) {
  const mapTeam = (t) => ({
    win: t.win,
    objectives: {
      kill: t.objectives?.kill ?? 0,
      tower: t.objectives?.tower ?? 0,
      dragon: t.objectives?.dragon ?? 0,
      baron: t.objectives?.baron ?? 0,
      herald: t.objectives?.herald ?? 0,
    },
    gold: t.gold || 0,
    players: (t.players || []).map((p) => ({
      sum: p.name,
      puuid: p.puuid,
      champ: (getChamp(p.championId) || {}).id || p.champion,
      role: p.role,
      kda: p.kda,
      cs: p.cs,
      dmg: p.damage,
      vis: p.vision,
      gold: p.gold,
      items: p.items || [],
      s1: p.summoner1Id,
      s2: p.summoner2Id,
      level: p.level,
      isMe: mePuuid ? p.puuid === mePuuid : false,
    })),
  });
  const blue = api.teams?.find((t) => t.teamId === 100) || api.teams?.[0];
  const red = api.teams?.find((t) => t.teamId === 200) || api.teams?.[1];
  return {
    id: api.id,
    queue: api.queue,
    duration: fmtDuration(api.durationSec),
    when: timeAgo(api.gameCreation),
    patch: api.patch,
    blueTeam: blue ? mapTeam(blue) : null,
    redTeam: red ? mapTeam(red) : null,
  };
}
